import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, AlertTriangle, TrendingUp, TrendingDown, Activity,
  ChevronLeft, ChevronRight, RefreshCw, Loader, Info, Zap,
  Target, Clock, DollarSign, BarChart3, Star, Bell,
  BookOpen, HelpCircle, X, Lightbulb, MousePointer
} from 'lucide-react';

/**
 * OpEx Calendar with GEX Predictions
 * 
 * Shows options expiration dates with predicted market behavior
 * based on gamma exposure analysis.
 */

const OpExCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [opexData, setOpexData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'list'
  const [alerts, setAlerts] = useState([]);
  
  // Mobile and education state
  const [isMobile, setIsMobile] = useState(false);
  const [showEducation, setShowEducation] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchOpExData();
  }, [currentDate]);

  const fetchOpExData = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await fetch(`${API_URL}/gamma/opex-calendar?year=${year}&month=${month}`);
      const data = await response.json();
      
      if (data.success) {
        setOpexData(data);
      }
    } catch (err) {
      console.error('OpEx data error:', err);
      // Generate default OpEx dates
      setOpexData(generateDefaultOpExData(currentDate));
    } finally {
      setLoading(false);
    }
  };

  // Generate default OpEx data if API fails
  const generateDefaultOpExData = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const events = [];

    // Find third Friday (monthly OpEx)
    const firstDay = new Date(year, month, 1);
    let thirdFriday = new Date(year, month, 1);
    
    // Find first Friday
    const dayOfWeek = firstDay.getDay();
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
    thirdFriday.setDate(1 + daysUntilFriday + 14); // Third Friday

    events.push({
      date: thirdFriday.toISOString().split('T')[0],
      type: 'monthly',
      name: 'Monthly OpEx',
      description: 'Standard monthly options expiration',
      impact: 'high',
      symbols: ['SPY', 'QQQ', 'IWM', 'All stocks'],
      gexPrediction: {
        expectedVolatility: 'High',
        pinningLikelihood: 85,
        keyLevels: [],
        tradingBias: 'Expect pinning to major strikes'
      }
    });

    // Weekly OpEx (every Friday)
    for (let week = 0; week < 5; week++) {
      const friday = new Date(year, month, 1);
      const firstFridayOffset = (5 - friday.getDay() + 7) % 7;
      friday.setDate(1 + firstFridayOffset + week * 7);
      
      if (friday.getMonth() === month && friday.getTime() !== thirdFriday.getTime()) {
        events.push({
          date: friday.toISOString().split('T')[0],
          type: 'weekly',
          name: 'Weekly OpEx',
          description: 'Weekly SPY/QQQ options expiration',
          impact: 'medium',
          symbols: ['SPY', 'QQQ', 'IWM'],
          gexPrediction: {
            expectedVolatility: 'Moderate',
            pinningLikelihood: 65,
            keyLevels: [],
            tradingBias: 'Watch for end-of-day volatility'
          }
        });
      }
    }

    // 0DTE (Monday, Wednesday, Friday for SPX)
    for (let day = 1; day <= 31; day++) {
      const d = new Date(year, month, day);
      if (d.getMonth() !== month) break;
      
      const dow = d.getDay();
      if (dow === 1 || dow === 3) { // Monday or Wednesday
        events.push({
          date: d.toISOString().split('T')[0],
          type: '0dte',
          name: '0DTE SPX',
          description: 'Same-day SPX options expiration',
          impact: 'low',
          symbols: ['SPX', 'XSP'],
          gexPrediction: {
            expectedVolatility: 'Variable',
            pinningLikelihood: 40,
            keyLevels: [],
            tradingBias: 'Intraday gamma effects'
          }
        });
      }
    }

    // Quarterly OpEx (Triple/Quadruple Witching)
    const quarterlyMonths = [2, 5, 8, 11]; // March, June, Sept, Dec (0-indexed)
    if (quarterlyMonths.includes(month)) {
      events.push({
        date: thirdFriday.toISOString().split('T')[0],
        type: 'quarterly',
        name: 'Quad Witching',
        description: 'Stock options, index options, index futures, stock futures expire',
        impact: 'extreme',
        symbols: ['All markets'],
        gexPrediction: {
          expectedVolatility: 'Very High',
          pinningLikelihood: 90,
          keyLevels: [],
          tradingBias: 'Massive gamma effects, expect high volume'
        }
      });
    }

    return {
      success: true,
      events: events.sort((a, b) => new Date(a.date) - new Date(b.date)),
      month: month + 1,
      year
    };
  };

  // Calendar generation
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startingDay = firstDayOfMonth.getDay();
    const totalDays = lastDayOfMonth.getDate();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  }, [currentDate]);

  // Get events for a specific date
  const getEventsForDate = (date) => {
    if (!opexData?.events) return [];
    const dateStr = date.toISOString().split('T')[0];
    return opexData.events.filter(e => e.date === dateStr);
  };

  // Navigation
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Get impact color
  const getImpactColor = (impact) => {
    switch (impact) {
      case 'extreme': return { bg: 'rgba(239, 68, 68, 0.3)', border: '#ef4444', text: '#fca5a5' };
      case 'high': return { bg: 'rgba(249, 115, 22, 0.3)', border: '#f97316', text: '#fdba74' };
      case 'medium': return { bg: 'rgba(234, 179, 8, 0.3)', border: '#eab308', text: '#fde047' };
      case 'low': return { bg: 'rgba(59, 130, 246, 0.2)', border: '#3b82f6', text: '#93c5fd' };
      default: return { bg: 'rgba(100, 116, 139, 0.2)', border: '#64748b', text: '#94a3b8' };
    }
  };

  // Get event icon
  const getEventIcon = (type) => {
    switch (type) {
      case 'quarterly': return <Zap size={14} />;
      case 'monthly': return <Star size={14} />;
      case 'weekly': return <Calendar size={14} />;
      case '0dte': return <Clock size={14} />;
      default: return <Activity size={14} />;
    }
  };

  // Upcoming events
  const upcomingEvents = useMemo(() => {
    if (!opexData?.events) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return opexData.events
      .filter(e => new Date(e.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  }, [opexData]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayNamesShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div style={{
      background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
      borderRadius: '1rem',
      overflow: 'hidden',
      border: '1px solid rgba(139, 92, 246, 0.2)'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15) 0%, rgba(234, 179, 8, 0.15) 100%)',
        borderBottom: '1px solid rgba(249, 115, 22, 0.3)',
        padding: '1.5rem 2rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #f97316 0%, #eab308 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
              }}>
                <Calendar size={28} color="white" />
              </div>
              <div>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  margin: 0,
                  background: 'linear-gradient(135deg, #fff 0%, #fbbf24 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  OpEx Calendar
                </h2>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                  Options Expiration & GEX Predictions
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={goToToday}
              style={{
                padding: '0.625rem 1rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem',
                color: '#94a3b8',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              Today
            </button>
            
            <button
              onClick={fetchOpExData}
              disabled={loading}
              style={{
                padding: '0.625rem 1rem',
                background: 'rgba(249, 115, 22, 0.2)',
                border: '1px solid rgba(249, 115, 22, 0.4)',
                borderRadius: '0.5rem',
                color: '#f97316',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '1rem' }}>
          {[
            { type: 'quarterly', label: 'Quad Witching', impact: 'extreme' },
            { type: 'monthly', label: 'Monthly OpEx', impact: 'high' },
            { type: 'weekly', label: 'Weekly OpEx', impact: 'medium' },
            { type: '0dte', label: '0DTE', impact: 'low' }
          ].map(item => {
            const colors = getImpactColor(item.impact);
            return (
              <div
                key={item.type}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.375rem 0.75rem',
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '2rem',
                  fontSize: '0.7rem',
                  color: colors.text
                }}
              >
                {getEventIcon(item.type)}
                {item.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 350px', gap: '1.5rem' }}>
          {/* Calendar */}
          <div>
            {/* Month Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <button
                onClick={() => navigateMonth(-1)}
                style={{
                  padding: '0.5rem',
                  background: 'rgba(71, 85, 105, 0.5)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                <ChevronLeft size={20} />
              </button>
              
              <h3 style={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: '600', margin: 0 }}>
                {isMobile 
                  ? `${monthNames[currentDate.getMonth()].slice(0, 3)} ${currentDate.getFullYear()}`
                  : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                }
              </h3>
              
              <button
                onClick={() => navigateMonth(1)}
                style={{
                  padding: '0.5rem',
                  background: 'rgba(71, 85, 105, 0.5)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Day Headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '0.25rem',
              marginBottom: '0.5rem'
            }}>
              {(isMobile ? dayNamesShort : dayNames).map((day, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: isMobile ? '0.25rem' : '0.5rem',
                    textAlign: 'center',
                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                    fontWeight: '600',
                    color: '#94a3b8'
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: isMobile ? '0.125rem' : '0.25rem'
            }}>
              {calendarDays.map((day, idx) => {
                const events = getEventsForDate(day.date);
                const isToday = day.date.toDateString() === new Date().toDateString();
                const isSelected = selectedDate && day.date.toDateString() === selectedDate.toDateString();
                const highestImpact = events.reduce((max, e) => {
                  const order = { extreme: 4, high: 3, medium: 2, low: 1 };
                  return order[e.impact] > order[max] ? e.impact : max;
                }, 'low');
                const colors = events.length > 0 ? getImpactColor(highestImpact) : null;

                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedDate(day.date)}
                    style={{
                      padding: isMobile ? '0.25rem' : '0.5rem',
                      minHeight: isMobile ? '50px' : '70px',
                      background: isSelected
                        ? 'rgba(139, 92, 246, 0.3)'
                        : colors?.bg || 'rgba(30, 41, 59, 0.3)',
                      border: isToday
                        ? '2px solid #60a5fa'
                        : isSelected
                        ? '2px solid #8b5cf6'
                        : colors
                        ? `1px solid ${colors.border}`
                        : '1px solid rgba(71, 85, 105, 0.3)',
                      borderRadius: isMobile ? '0.375rem' : '0.5rem',
                      cursor: 'pointer',
                      opacity: day.isCurrentMonth ? 1 : 0.4,
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{
                      fontSize: isMobile ? '0.7rem' : '0.875rem',
                      fontWeight: isToday ? '700' : '500',
                      color: isToday ? '#60a5fa' : day.isCurrentMonth ? '#e2e8f0' : '#64748b',
                      marginBottom: '0.125rem'
                    }}>
                      {day.date.getDate()}
                    </div>
                    
                    {events.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                        {events.slice(0, isMobile ? 1 : 2).map((event, eIdx) => {
                          const eventColors = getImpactColor(event.impact);
                          return (
                            <div
                              key={eIdx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.125rem',
                                fontSize: isMobile ? '0.5rem' : '0.6rem',
                                color: eventColors.text,
                                padding: '0.125rem',
                                background: 'rgba(0,0,0,0.2)',
                                borderRadius: '0.25rem'
                              }}
                            >
                              {!isMobile && getEventIcon(event.type)}
                              <span style={{ 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                maxWidth: isMobile ? '30px' : '60px'
                              }}>
                                {isMobile 
                                  ? (event.type === 'quarterly' ? 'Q' : event.type === 'monthly' ? 'M' : event.type === 'weekly' ? 'W' : '0D')
                                  : (event.type === 'quarterly' ? 'Quad' : event.type === 'monthly' ? 'Monthly' : event.type)
                                }
                              </span>
                            </div>
                          );
                        })}
                        {events.length > (isMobile ? 1 : 2) && (
                          <div style={{ fontSize: isMobile ? '0.45rem' : '0.6rem', color: '#64748b' }}>
                            +{events.length - (isMobile ? 1 : 2)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Selected Date Details */}
            {selectedDate && (
              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(71, 85, 105, 0.5)',
                borderRadius: '0.75rem',
                padding: '1.25rem',
                marginBottom: '1rem'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600' }}>
                  {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </h4>
                
                {getEventsForDate(selectedDate).length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {getEventsForDate(selectedDate).map((event, idx) => {
                      const colors = getImpactColor(event.impact);
                      return (
                        <div
                          key={idx}
                          style={{
                            background: colors.bg,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '0.5rem',
                            padding: '1rem'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.5rem'
                          }}>
                            {getEventIcon(event.type)}
                            <span style={{ fontWeight: '600', color: colors.text }}>
                              {event.name}
                            </span>
                            <span style={{
                              marginLeft: 'auto',
                              padding: '0.125rem 0.5rem',
                              background: 'rgba(0,0,0,0.2)',
                              borderRadius: '1rem',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              color: colors.text
                            }}>
                              {event.impact}
                            </span>
                          </div>
                          
                          <p style={{ 
                            margin: '0 0 0.75rem 0', 
                            fontSize: '0.8rem', 
                            color: '#cbd5e1',
                            lineHeight: '1.5'
                          }}>
                            {event.description}
                          </p>
                          
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                            <strong>Symbols:</strong> {event.symbols?.join(', ')}
                          </div>
                          
                          {event.gexPrediction && (
                            <div style={{
                              background: 'rgba(0,0,0,0.2)',
                              borderRadius: '0.375rem',
                              padding: '0.75rem',
                              marginTop: '0.5rem'
                            }}>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#94a3b8', 
                                marginBottom: '0.5rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                GEX Prediction
                              </div>
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: '1fr 1fr', 
                                gap: '0.5rem',
                                fontSize: '0.75rem'
                              }}>
                                <div>
                                  <span style={{ color: '#64748b' }}>Volatility: </span>
                                  <span style={{ color: '#e2e8f0' }}>{event.gexPrediction.expectedVolatility}</span>
                                </div>
                                <div>
                                  <span style={{ color: '#64748b' }}>Pinning: </span>
                                  <span style={{ color: '#e2e8f0' }}>{event.gexPrediction.pinningLikelihood}%</span>
                                </div>
                              </div>
                              <div style={{ 
                                marginTop: '0.5rem', 
                                fontSize: '0.75rem',
                                color: '#fbbf24'
                              }}>
                                💡 {event.gexPrediction.tradingBias}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '1.5rem', 
                    color: '#64748b',
                    fontSize: '0.875rem'
                  }}>
                    No expiration events on this day
                  </div>
                )}
              </div>
            )}

            {/* Upcoming Events */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(71, 85, 105, 0.5)',
              borderRadius: '0.75rem',
              padding: '1.25rem'
            }}>
              <h4 style={{ 
                margin: '0 0 1rem 0', 
                fontSize: '0.9rem', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Clock size={16} color="#f97316" />
                Upcoming Events
              </h4>
              
              {upcomingEvents.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {upcomingEvents.map((event, idx) => {
                    const colors = getImpactColor(event.impact);
                    const eventDate = new Date(event.date);
                    const daysUntil = Math.ceil((eventDate - new Date()) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <div
                        key={idx}
                        onClick={() => setSelectedDate(eventDate)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          background: 'rgba(15, 23, 42, 0.5)',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '0.5rem',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          background: colors.bg,
                          borderRadius: '0.5rem',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <div style={{ fontSize: '0.65rem', color: colors.text }}>
                            {eventDate.toLocaleDateString('en-US', { month: 'short' })}
                          </div>
                          <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.text }}>
                            {eventDate.getDate()}
                          </div>
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: '0.8rem', 
                            fontWeight: '600', 
                            color: '#e2e8f0',
                            marginBottom: '0.125rem'
                          }}>
                            {event.name}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                            {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                          </div>
                        </div>
                        
                        <div style={{
                          padding: '0.25rem 0.5rem',
                          background: colors.bg,
                          borderRadius: '0.25rem',
                          fontSize: '0.6rem',
                          color: colors.text,
                          textTransform: 'uppercase'
                        }}>
                          {event.impact}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '1rem', 
                  color: '#64748b',
                  fontSize: '0.875rem'
                }}>
                  No upcoming events
                </div>
              )}
            </div>

            {/* Trading Tips */}
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.5rem'
            }}>
              <h5 style={{ 
                margin: '0 0 0.5rem 0', 
                fontSize: '0.8rem', 
                color: '#93c5fd',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Info size={14} />
                OpEx Trading Tips
              </h5>
              <ul style={{ 
                margin: 0, 
                paddingLeft: '1.25rem', 
                fontSize: '0.75rem', 
                color: '#cbd5e1',
                lineHeight: '1.6'
              }}>
                <li>Max Pain often acts as a magnet on OpEx days</li>
                <li>Expect increased volatility in final 2 hours</li>
                <li>Large GEX = stronger pinning effect</li>
                <li>Watch for gamma squeeze potential near key strikes</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// EDUCATION PANEL
// ============================================

const OpExEducation = ({ onClose, isMobile }) => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
    borderBottom: '1px solid rgba(16, 185, 129, 0.3)',
    padding: isMobile ? '1rem' : '1.5rem 2rem'
  }}>
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '1rem' 
    }}>
      <h3 style={{ 
        margin: 0, 
        fontSize: isMobile ? '1rem' : '1.25rem', 
        color: '#10b981',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <BookOpen size={isMobile ? 18 : 22} />
        Understanding OpEx Events
      </h3>
      <button
        onClick={onClose}
        style={{
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '50%',
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#ef4444'
        }}
      >
        <X size={16} />
      </button>
    </div>
    
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: isMobile ? '0.75rem' : '1rem'
    }}>
      {/* Quad Witching */}
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '0.75rem',
        padding: isMobile ? '0.75rem' : '1rem'
      }}>
        <h4 style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: isMobile ? '0.8rem' : '0.9rem', 
          color: '#fca5a5',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Zap size={16} />
          Quad Witching
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: isMobile ? '0.7rem' : '0.8rem', 
          color: '#94a3b8',
          lineHeight: '1.5'
        }}>
          Four types of contracts expire: stock options, index options, index futures, and stock futures. 
          Expect extreme volume and volatility. Occurs 4x/year (March, June, Sept, Dec).
        </p>
      </div>

      {/* Monthly OpEx */}
      <div style={{
        background: 'rgba(249, 115, 22, 0.1)',
        border: '1px solid rgba(249, 115, 22, 0.3)',
        borderRadius: '0.75rem',
        padding: isMobile ? '0.75rem' : '1rem'
      }}>
        <h4 style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: isMobile ? '0.8rem' : '0.9rem', 
          color: '#fdba74',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Star size={16} />
          Monthly OpEx
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: isMobile ? '0.7rem' : '0.8rem', 
          color: '#94a3b8',
          lineHeight: '1.5'
        }}>
          Third Friday of each month. Standard equity options expire. High volume day with 
          strong pinning effects. Watch Max Pain levels closely.
        </p>
      </div>

      {/* Weekly OpEx */}
      <div style={{
        background: 'rgba(234, 179, 8, 0.1)',
        border: '1px solid rgba(234, 179, 8, 0.3)',
        borderRadius: '0.75rem',
        padding: isMobile ? '0.75rem' : '1rem'
      }}>
        <h4 style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: isMobile ? '0.8rem' : '0.9rem', 
          color: '#fde047',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Calendar size={16} />
          Weekly OpEx
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: isMobile ? '0.7rem' : '0.8rem', 
          color: '#94a3b8',
          lineHeight: '1.5'
        }}>
          Every Friday for SPY, QQQ, IWM. Moderate gamma effects. 
          End-of-day volatility common as positions are rolled or closed.
        </p>
      </div>

      {/* 0DTE */}
      <div style={{
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '0.75rem',
        padding: isMobile ? '0.75rem' : '1rem'
      }}>
        <h4 style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: isMobile ? '0.8rem' : '0.9rem', 
          color: '#93c5fd',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Clock size={16} />
          0DTE (Zero Days to Expiration)
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: isMobile ? '0.7rem' : '0.8rem', 
          color: '#94a3b8',
          lineHeight: '1.5'
        }}>
          SPX options expire Mon/Wed/Fri. Intraday gamma effects can cause rapid price moves. 
          Popular with day traders seeking leveraged exposure.
        </p>
      </div>
    </div>

    {/* Key insight */}
    <div style={{
      marginTop: '1rem',
      padding: isMobile ? '0.75rem' : '1rem',
      background: 'rgba(16, 185, 129, 0.15)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      borderRadius: '0.75rem',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem'
    }}>
      <Lightbulb size={20} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
      <div>
        <strong style={{ color: '#10b981', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>Key Insight:</strong>
        <span style={{ color: '#94a3b8', fontSize: isMobile ? '0.7rem' : '0.8rem', marginLeft: '0.5rem' }}>
          OpEx days see "pinning" where stocks gravitate toward strikes with high open interest. 
          The larger the GEX, the stronger this effect. Plan entries/exits around OpEx to avoid 
          being caught in gamma-driven moves.
        </span>
      </div>
    </div>
  </div>
);

// Add spin animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin {
    animation: spin 1s linear infinite;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}

export default OpExCalendar;